import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API } from "../data/site";
import { Stagger, StaggerItem, FadeUp } from "../components/motion";
import PageHeader from "../components/PageHeader";
import HomeCard from "../components/HomeCard";
import SEO from "../components/SEO";

const FILTERS = ["All", "Available", "Coming Soon", "Under Construction", "Sold"];
const STATUS_ORDER = { "Available": 0, "Coming Soon": 1, "Under Construction": 2, "Sold": 3 };

const AvailableHomes = () => {
  const [filter, setFilter] = useState("All");
  const { data: homes = [], isLoading } = useQuery({
    queryKey: ["homes"],
    queryFn: async () => (await axios.get(`${API}/homes`)).data,
  });

  const filtered = (filter === "All" ? homes : homes.filter((h) => h.status === filter))
    .slice()
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  return (
    <main>
      <SEO />
      <PageHeader
        testid="available-homes-header"
        eyebrow="New Construction Homes in Idaho"
        lines={[{ text: "Available" }, { text: "Homes", italic: true }]}
        description="Thoughtfully designed new construction homes by On Point Homes — ready now, coming soon, and under construction across the Treasure Valley."
      />

      <section className="pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <FadeUp className="flex flex-wrap gap-3 mb-12 md:mb-16" data-testid="homes-filter-bar">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`font-mono text-[0.65rem] tracking-[0.18em] uppercase font-bold px-5 py-2.5 border transition-colors duration-300 ${
                  filter === f
                    ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
                    : "border-[#1A1A1A]/25 text-[#5C5A56] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                }`}
              >
                {f}
              </button>
            ))}
          </FadeUp>

          {isLoading ? (
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#5C5A56]" data-testid="homes-loading">Loading homes…</p>
          ) : filtered.length === 0 ? (
            <div className="py-24 border-t border-[rgba(26,26,26,0.12)]" data-testid="homes-empty">
              <p className="font-serif italic text-3xl text-[#A3A09B]">No homes in this category right now.</p>
              <p className="mt-4 text-[#5C5A56] max-w-md">Inventory changes often — reach out and we'll let you know what's coming next.</p>
            </div>
          ) : (
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14" data-testid="homes-grid">
              {filtered.map((home) => (
                <StaggerItem key={home.id}>
                  <HomeCard home={home} />
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>
    </main>
  );
};

export default AvailableHomes;
